import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const EXIT_MS = 150; // keep in sync with the modal-out / overlay-out durations in tailwind.config.js

export default function Modal({ title, onClose, children, wide = false }) {
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // × and Escape play the exit animation first. Callers that unmount the modal themselves
  // (Cancel / Save) still close instantly.
  const requestClose = useCallback(() => {
    if (closeTimer.current) return;
    setClosing(true);
    closeTimer.current = setTimeout(() => onCloseRef.current(), EXIT_MS);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && requestClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose]);

  // Portal to <body>: as a child of a page wrapper, Tailwind's space-y-* gave this fixed overlay a
  // margin-top, leaving an undimmed strip at the top of the screen.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto">
      <div className={`absolute inset-0 bg-black/30 ${closing ? 'animate-overlay-out' : 'animate-overlay-in'}`} />
      <div className={`relative bg-white rounded-2xl shadow-xl w-full flex flex-col max-h-[calc(100vh-3rem)] ${wide ? 'max-w-2xl' : 'max-w-md'} ${
        closing ? 'animate-modal-out pointer-events-none' : 'animate-modal-in'
      }`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            onClick={requestClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
}
